import { useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  DialogHeader,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { AmountField } from '@/components/ui/AmountField';
import { DateField, DateRangeField } from '@/components/ui/DateField';
import { SearchSelect } from '@/components/ui/SearchSelect';
import { TagInput } from '@/components/ui/TagInput';
import { FileDropzone } from '@/components/ui/FileDropzone';
import { FileChip } from '@/components/ui/FileChip';
import {
  displayDateInput,
  parseDateInput,
  formatAmountInput,
  parseAmountInput,
} from '@/components/ui/fieldData';
import type { Attachment } from '@/lib/attachments';
import { zoneOffset } from '@/lib/eventTime';
import {
  validateFieldValue,
  getZonedInput,
  resolveZonedInput,
  type FieldDefinition,
  type TypedFieldValue,
  type JsonValue,
} from './typedFieldData';
import type { FieldRegistry, FieldCommit, SecretAccess, CustomDefinition } from './fieldRegistry';

function initialInputs(initial: TypedFieldValue, definition: FieldDefinition) {
  const zoned =
    initial.type === 'deadline' && initial.value
      ? getZonedInput(
          initial.value,
          definition.type === 'deadline' ? (definition.timeZone ?? 'Europe/Moscow') : 'Europe/Moscow',
        )
      : null;
  const primary =
    initial.type === 'money' && initial.value
      ? formatAmountInput(initial.value.minorUnits, initial.value.currency)
      : initial.type === 'date'
        ? displayDateInput(initial.value ?? '')
        : initial.type === 'period'
          ? displayDateInput(initial.value?.from ?? '')
          : initial.type === 'deadline'
            ? displayDateInput(zoned?.day ?? '')
            : initial.type === 'person' || initial.type === 'entity'
              ? (initial.value?.id ?? '')
              : initial.type === 'secret'
                ? ''
                : typeof initial.value === 'string' || typeof initial.value === 'number'
                  ? String(initial.value)
                  : '';
  return {
    primary,
    secondary: initial.type === 'period' ? displayDateInput(initial.value?.to ?? '') : (zoned?.time ?? ''),
  };
}
export function FieldEditorForm({
  definition,
  initial,
  onCommit,
  onClose,
  registry = {},
  secret,
}: {
  definition: FieldDefinition;
  initial: TypedFieldValue;
  onCommit: FieldCommit;
  onClose: () => void;
  registry?: FieldRegistry;
  secret?: SecretAccess;
}) {
  const [openedAt] = useState(Date.now);
  const start = initialInputs(initial, definition);
  const [primary, setPrimary] = useState(start.primary),
    [secondary, setSecondary] = useState(start.secondary),
    [dirty, setDirty] = useState(false),
    [clear, setClear] = useState(false);
  const [tags, setTags] = useState(initial.type === 'tags' ? (initial.value ?? []) : []),
    [tagInput, setTagInput] = useState(''),
    [files, setFiles] = useState<Attachment[]>(initial.type === 'file' ? (initial.value ?? []) : []);
  const [checked, setChecked] = useState<boolean | null>(initial.type === 'checkbox' ? initial.value : null),
    [custom, setCustom] = useState<JsonValue>(initial.value as JsonValue),
    [offset, setOffset] = useState('');
  const [error, setError] = useState<string>(),
    [saving, setSaving] = useState(false);
  const change = (value: string) => {
    setPrimary(value);
    setDirty(true);
    setClear(false);
    setError(undefined);
    setOffset('');
  };
  const second = (value: string) => {
    setSecondary(value);
    setDirty(true);
    setClear(false);
    setError(undefined);
    setOffset('');
  };
  const zone = definition.type === 'deadline' ? (definition.timeZone ?? 'Europe/Moscow') : 'Europe/Moscow';
  const day = definition.type === 'deadline' ? parseDateInput(primary).day : null;
  const resolved = definition.type === 'deadline' && day ? resolveZonedInput(day, secondary, zone) : null;
  const customAdapter = registry[definition.type],
    CustomEditor = customAdapter?.Editor;
  const options =
    'options' in definition
      ? definition.options.map((option) => ({
          value: option.id,
          label: option.label,
          disabled: 'disabled' in option ? option.disabled : undefined,
        }))
      : [];
  if (primary && !options.some((o) => o.value === primary))
    options.unshift({ value: primary, label: 'Недоступно: ' + primary, disabled: true });
  const makeValue = (): { value?: TypedFieldValue; error?: string } => {
    if (clear) return { value: { type: definition.type, value: null } as TypedFieldValue };
    if (!dirty) return { value: initial };
    let value: unknown = primary;
    switch (definition.type) {
      case 'number': {
        const raw = primary
          .replace(/[ \u00a0\u202f]/g, '')
          .replace('−', '-')
          .replace(',', '.');
        if (!raw) value = null;
        else if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw))
          return { error: 'Укажи число целиком.' };
        else value = Number(raw);
        break;
      }
      case 'money': {
        const parsed = parseAmountInput(primary, definition.currency);
        if (parsed.error) return { error: parsed.error };
        value =
          parsed.minorUnits === null
            ? null
            : { minorUnits: parsed.minorUnits, currency: definition.currency };
        break;
      }
      case 'date': {
        const parsed = parseDateInput(primary);
        if (parsed.error) return { error: parsed.error };
        value = parsed.day;
        break;
      }
      case 'period': {
        const from = parseDateInput(primary),
          to = parseDateInput(secondary);
        if (from.error || to.error) return { error: from.error ?? to.error ?? undefined };
        if (!from.day && !to.day) value = null;
        else if (!from.day || !to.day) return { error: 'Укажи обе границы периода.' };
        else value = { from: from.day, to: to.day };
        break;
      }
      case 'deadline': {
        if (!primary && !secondary) value = null;
        else if (!day) return { error: parseDateInput(primary).error ?? 'Укажи дату.' };
        else if (resolved?.error) return { error: resolved.error };
        else if (resolved?.choices.length === 1) value = resolved.choices[0].at;
        else if (offset && resolved?.choices.some((c) => c.at === offset)) value = offset;
        else return { error: 'Выбери смещение для этого времени.' };
        break;
      }
      case 'person':
      case 'entity':
        value = primary ? (definition.options.find((o) => o.id === primary) ?? null) : null;
        break;
      case 'status':
      case 'select':
      case 'importance':
        value = primary || null;
        break;
      case 'tags':
        if (tagInput.trim()) return { error: 'Добавь набранный тег клавишей Enter или очисти ввод.' };
        value = tags;
        break;
      case 'checkbox':
        value = checked;
        break;
      case 'file':
        value = files;
        break;
      default:
        if (definition.type.startsWith('custom:')) value = custom;
    }
    return { value: { type: definition.type, value } as TypedFieldValue };
  };
  const save = async () => {
    setError(undefined);
    if (definition.type === 'secret') {
      if (!primary.trim()) {
        setError('Введи новое значение секрета.');
        return;
      }
      if (primary.length > 4096) {
        setError('Секрет слишком длинный.');
        return;
      }
      if (!secret?.change) {
        setError('Нет права на изменение.');
        return;
      }
      setSaving(true);
      try {
        await secret.change(primary);
        setPrimary('');
        onClose();
      } catch {
        setError('Не удалось сохранить. Введённое значение осталось здесь.');
      } finally {
        setSaving(false);
      }
      return;
    }
    const candidate = makeValue();
    if (candidate.error || !candidate.value) {
      setError(candidate.error ?? 'Проверь значение.');
      return;
    }
    const issue =
      validateFieldValue(definition, candidate.value) ??
      (definition.type.startsWith('custom:')
        ? !customAdapter
          ? 'Тип поля не подключён.'
          : candidate.value.value === null
            ? null
            : customAdapter.validate(candidate.value.value as JsonValue, definition as CustomDefinition)
        : null);
    if (issue) {
      setError(issue);
      return;
    }
    setSaving(true);
    try {
      await onCommit(definition.id, candidate.value, initial);
      onClose();
    } catch {
      setError('Не удалось сохранить. Ввод сохранён в этом окне; попробуй ещё раз.');
    } finally {
      setSaving(false);
    }
  };
  const addFiles = (picked: File[]) => {
    setFiles((old) =>
      old.concat(
        picked.map((file) => ({
          id: 'local-' + crypto.randomUUID(),
          name: file.name,
          size: file.size,
          mime: file.type || 'application/octet-stream',
          state: 'ready' as const,
        })),
      ),
    );
    setDirty(true);
    setClear(false);
  };
  let control;
  switch (definition.type) {
    case 'text':
      control = (
        <Input
          label={definition.label}
          value={primary}
          onChange={(e) => change(e.target.value)}
          maxLength={definition.maxLength ?? 4000}
          disabled={saving}
        />
      );
      break;
    case 'multiline':
      control = (
        <Textarea
          label={definition.label}
          value={primary}
          onChange={(e) => change(e.target.value)}
          maxLength={definition.maxLength ?? 16000}
          rows={5}
          disabled={saving}
        />
      );
      break;
    case 'number':
      control = (
        <Input
          label={definition.label}
          value={primary}
          inputMode="decimal"
          onChange={(e) => change(e.target.value)}
          disabled={saving}
        />
      );
      break;
    case 'money':
      control = (
        <AmountField
          label={definition.label + ' · ' + definition.currency}
          value={primary}
          currency={definition.currency}
          onChange={change}
          disabled={saving}
        />
      );
      break;
    case 'date':
      control = <DateField label={definition.label} value={primary} onChange={change} disabled={saving} />;
      break;
    case 'period':
      control = (
        <DateRangeField
          label={definition.label}
          value={{ from: primary, to: secondary }}
          onChange={(next) => {
            change(next.from);
            second(next.to);
          }}
          disabled={saving}
        />
      );
      break;
    case 'deadline':
      control = (
        <div className="space-y-4">
          <DateField label="Дата" value={primary} onChange={change} disabled={saving} timeZone={zone} />
          <Input
            label="Время"
            type="time"
            value={secondary}
            onChange={(e) => second(e.target.value)}
            disabled={saving}
          />
          <p className="text-xs text-fg-muted">
            {zone === 'Europe/Moscow' ? 'Москва' : zone} ·{' '}
            {zoneOffset(
              new Date(
                offset ||
                  resolved?.choices[0]?.at ||
                  (initial.type === 'deadline' && initial.value ? initial.value : openedAt),
              ),
              zone,
            )}
          </p>
          {resolved && resolved.choices.length > 1 && (
            <Select
              value={offset}
              onValueChange={(v) => {
                setOffset(v);
                setDirty(true);
                setClear(false);
              }}
            >
              <SelectTrigger aria-label="Смещение времени">
                <SelectValue placeholder="Выбрать смещение" />
              </SelectTrigger>
              <SelectContent>
                {resolved.choices.map((choice) => (
                  <SelectItem key={choice.at} value={choice.at}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      );
      break;
    case 'person':
    case 'entity':
    case 'status':
    case 'select':
    case 'importance':
      control = (
        <SearchSelect
          label={definition.label}
          value={primary || null}
          onChange={(v) => change(v ?? '')}
          options={options}
          disabled={saving}
        />
      );
      break;
    case 'tags':
      control = (
        <TagInput
          label={definition.label}
          value={tags}
          onChange={(v) => {
            setTags(v);
            setDirty(true);
            setClear(false);
          }}
          inputValue={tagInput}
          onInputChange={(v) => {
            setTagInput(v);
            setDirty(true);
            setClear(false);
          }}
          maxTags={definition.maxTags ?? 8}
          disabled={saving}
        />
      );
      break;
    case 'checkbox':
      control = (
        <Checkbox
          label={definition.label}
          checked={checked === null ? 'indeterminate' : checked}
          onCheckedChange={(v) => {
            setChecked(v === true);
            setDirty(true);
            setClear(false);
          }}
          disabled={saving}
        />
      );
      break;
    case 'file':
      control = (
        <div className="space-y-3">
          <FileDropzone label={definition.label} onFiles={addFiles} disabled={saving} />
          {files.map((file) => (
            <div key={file.id} className="space-y-1">
              <FileChip file={file} />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => {
                  setFiles((old) => old.filter((f) => f.id !== file.id));
                  setDirty(true);
                  setClear(false);
                }}
              >
                Убрать {file.name}
              </Button>
            </div>
          ))}
        </div>
      );
      break;
    case 'secret':
      control = (
        <Input
          label="Новое значение"
          type="password"
          value={primary}
          onChange={(e) => change(e.target.value)}
          autoComplete="new-password"
          maxLength={4096}
          disabled={saving}
        />
      );
      break;
    case 'link':
    case 'email':
    case 'phone':
      control = (
        <Input
          label={definition.label}
          type={definition.type === 'email' ? 'email' : definition.type === 'phone' ? 'tel' : 'url'}
          value={primary}
          onChange={(e) => change(e.target.value)}
          disabled={saving}
        />
      );
      break;
    default:
      control = CustomEditor ? (
        <CustomEditor
          definition={definition as CustomDefinition}
          label={definition.label}
          value={custom}
          onChange={(v) => {
            setCustom(v);
            setDirty(true);
            setClear(false);
          }}
          disabled={saving}
        />
      ) : (
        <p className="text-sm text-fg-muted">Тип поля не подключён. Значение останется без изменений.</p>
      );
  }
  return (
    <>
      <DialogHeader
        title={'Изменить: ' + definition.label}
        description={definition.help ?? 'Изменение одного свойства объекта.'}
      />
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        aria-busy={saving}
        className="min-h-0 space-y-4 overflow-y-auto px-6 pb-6"
      >
        {control}
        {clear && <p className="text-xs text-fg-muted">Значение будет очищено.</p>}
        {error && (
          <p role="alert" data-field-error="" className="text-sm text-danger-strong">
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
          <Button
            type="submit"
            onMouseDown={(event) => {
              if (event.button === 0) event.preventDefault();
            }}
            size="sm"
            disabled={saving || (definition.type.startsWith('custom:') && !customAdapter)}
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button type="button" size="sm" variant="secondary" disabled={saving} onClick={onClose}>
            Отмена
          </Button>
          {!definition.required && definition.type !== 'secret' && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => {
                setClear(true);
                setDirty(true);
                setError(undefined);
              }}
            >
              Очистить
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
